import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Box, useMediaQuery, useTheme as useMuiTheme,
  Avatar, Menu, MenuItem, Divider, Tooltip, Button, Dialog, DialogTitle,
  DialogContent, Radio, RadioGroup, FormControlLabel, FormControl, Collapse
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon,
  VideoLibrary as VideoIcon, Add as AddIcon,
  Settings as SettingsIcon, People as PeopleIcon,
  Badge as BadgeIcon, Logout, Person, Movie as MovieIcon,
  Edit as EditIcon, Lock as LockIcon, Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon, Palette as PaletteIcon,
  Assessment as FinanceHubIcon,
  CheckBox as TodoIcon, EmojiEvents as AwardIcon, CalendarMonth as CalendarIcon,
  Inventory as InventoryIcon, Apartment as PropertyIcon, PersonSearch as RecruitmentIcon,
  HowToReg as OnboardingIcon,
  Groups as MeetingsIcon,
  Payments as PaymentsIcon,
  NotificationsActive as NotificationsIcon,
  ManageHistory as AuditIcon,
  EventNote as ScheduleIcon,
  MovieFilter as PlannerIcon,
  DateRange as ShootCalIcon,
  ImportContacts as ContactsIcon,
  Campaign as AdIcon,
  ExpandLess, ExpandMore,
  ChevronLeft, ChevronRight,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ChangePasswordDialog from './ChangePasswordDialog';

const SIDEBAR_FULL = 240;
const SIDEBAR_MINI = 64;

const NAV_GROUPS = [
  { label: 'Content',    paths: ['/', '/content', '/content/new', '/shoot-planner', '/shoot-calendar'] },
  { label: 'Team',       paths: ['/manager', '/director', '/editor', '/employee', '/my-schedule', '/todos', '/meetings'] },
  { label: 'HR & Finance', paths: ['/attendance', '/salary', '/expenses', '/leave-calendar', '/recruitment', '/onboarding', '/inventory', '/awards'] },
  { label: 'Business',   paths: ['/ads', '/contacts', '/property'] },
  { label: 'Admin',      paths: ['/users', '/settings', '/notifications', '/access-control', '/content/audit', '/user-activity', '/finance-hub'] },
];

function GroupedNav({ groups, navigate, location, isMobile, setMobileOpen, collapsed }) {
  const activeGroup = groups.findIndex(g => g.items.some(i => location.pathname === i.path));

  const [open, setOpen] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('navGroups') || '{}');
      const defaults = {};
      groups.forEach((_, i) => { defaults[i] = saved[i] !== undefined ? saved[i] : true; });
      return defaults;
    } catch { return groups.reduce((a, _, i) => ({ ...a, [i]: true }), {}); }
  });

  const toggle = (idx) => {
    setOpen(prev => {
      const next = { ...prev, [idx]: !prev[idx] };
      localStorage.setItem('navGroups', JSON.stringify(next));
      return next;
    });
  };

  const [autoExpanded, setAutoExpanded] = useState(false);
  if (!autoExpanded && activeGroup !== -1 && !open[activeGroup]) {
    setOpen(prev => ({ ...prev, [activeGroup]: true }));
    setAutoExpanded(true);
  }

  if (collapsed) {
    // Mini mode — icons only, no group headers
    return (
      <>
        {groups.map(group =>
          group.items.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip key={item.text} title={item.text} placement="right" arrow>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                    selected={isActive}
                    sx={{
                      justifyContent: 'center', minHeight: 44, px: 0,
                      '&.Mui-selected': { backgroundColor: 'primary.light', '&:hover': { backgroundColor: 'primary.light' } }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })
        )}
      </>
    );
  }

  return (
    <>
      {groups.map((group, gIdx) => (
        <Box key={group.label}>
          <ListItemButton onClick={() => toggle(gIdx)} sx={{ py: 0.5, px: 2, minHeight: 36 }}>
            <ListItemText
              primary={group.label}
              primaryTypographyProps={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', textTransform: 'uppercase' }}
            />
            {open[gIdx] ? <ExpandLess sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 16, color: 'text.secondary' }} />}
          </ListItemButton>
          <Collapse in={!!open[gIdx]} timeout="auto" unmountOnExit>
            {group.items.map(item => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  selected={location.pathname === item.path}
                  sx={{ py: 0.5, pl: 3, minHeight: 38, '&.Mui-selected': { backgroundColor: 'primary.light', '&:hover': { backgroundColor: 'primary.light' } } }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.83rem', lineHeight: 1.3 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </Collapse>
          <Divider sx={{ opacity: 0.4, my: 0.25 }} />
        </Box>
      ))}
    </>
  );
}

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarCollapsed') || 'false'); } catch { return false; }
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { user, logout, canViewPath, permsLoaded } = useAuth();
  const { currentTheme, setCurrentTheme, themes } = useTheme();

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_MINI : SIDEBAR_FULL;

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
      return next;
    });
  };

  const handleMenuOpen = (event) => { setAnchorEl(event.currentTarget); };
  const handleMenuClose = () => { setAnchorEl(null); };
  const handleProfile = () => { handleMenuClose(); navigate('/employee'); };
  const handleChangePassword = () => { handleMenuClose(); setPasswordDialogOpen(true); };
  const handleLogout = () => { handleMenuClose(); logout(); navigate('/login'); };

  const allMenuItems = [
    { text: 'Dashboard',           icon: <DashboardIcon />,       path: '/',              roles: ['owner', 'manager', 'lead'] },
    { text: 'Content Library',     icon: <VideoIcon />,           path: '/content',       roles: ['owner', 'manager', 'lead'] },
    { text: 'Add Content',         icon: <AddIcon />,             path: '/content/new',   roles: ['manager', 'director', 'hr', 'lead'] },
    { text: 'Employee Dashboard',  icon: <BadgeIcon />,           path: '/employee',      roles: ['all'] },
    { text: 'My Schedule',         icon: <ScheduleIcon />,        path: '/my-schedule',   roles: ['all'] },
    { text: 'Shoot Planner',       icon: <PlannerIcon />,         path: '/shoot-planner', roles: ['owner', 'manager', 'lead', 'director'] },
    { text: 'Shoot Calendar',      icon: <ShootCalIcon />,        path: '/shoot-calendar',roles: ['owner', 'manager', 'lead', 'director'] },
    { text: 'Manager Dashboard',   icon: <AssessmentIcon />,      path: '/manager',       roles: ['owner', 'manager', 'hr', 'lead'] },
    { text: "Director's Dashboard",icon: <MovieIcon />,           path: '/director',      roles: ['director'] },
    { text: "Editor's Dashboard",  icon: <EditIcon />,            path: '/editor',        roles: ['editor'] },
    { text: 'Todo App',            icon: <TodoIcon />,            path: '/todos',         roles: ['all'] },
    { text: 'Meetings',            icon: <MeetingsIcon />,        path: '/meetings',      roles: ['all'] },
    { text: 'Expense Management',  icon: <AccountBalanceIcon />,  path: '/expenses',      roles: ['owner', 'finance', 'manager', 'hr'] },
    { text: 'Awards & Recognition',icon: <AwardIcon />,           path: '/awards',        roles: ['all'] },
    { text: 'Attendance',          icon: <CalendarIcon />,        path: '/attendance',    roles: ['owner', 'manager', 'hr', 'lead'] },
    { text: 'Employee Salary',     icon: <PaymentsIcon />,        path: '/salary',        roles: ['owner', 'manager', 'hr'] },
    { text: 'Inventory Management',icon: <InventoryIcon />,       path: '/inventory',     roles: ['owner', 'manager', 'hr', 'lead'] },
    { text: 'Recruitment',         icon: <RecruitmentIcon />,     path: '/recruitment',   roles: ['owner', 'manager', 'hr', 'lead'] },
    { text: 'Onboarding',          icon: <OnboardingIcon />,      path: '/onboarding',    roles: ['owner', 'manager', 'hr', 'lead'] },
    { text: 'Leave Calendar',      icon: <CalendarIcon />,        path: '/leave-calendar',roles: ['owner', 'manager', 'hr'] },
    { text: 'User Management',     icon: <PeopleIcon />,          path: '/users',         roles: ['owner', 'manager', 'hr'] },
    { text: 'Configure',           icon: <SettingsIcon />,        path: '/settings',      roles: ['owner', 'manager'] },
    { text: 'Contact Diary',       icon: <ContactsIcon />,        path: '/contacts',      roles: ['owner', 'manager'] },
    { text: 'Ad / Collaboration',  icon: <AdIcon />,              path: '/ads',           roles: ['owner', 'manager'] },
    { text: 'Property Management', icon: <PropertyIcon />,        path: '/property',      roles: ['owner', 'manager', 'hr', 'lead'] },
    { text: 'Access Control',      icon: <LockIcon />,            path: '/access-control', roles: ['owner'] },
    { text: 'Notifications',       icon: <NotificationsIcon />,   path: '/notifications', roles: ['owner'] },
    { text: 'Finance Hub',         icon: <FinanceHubIcon />,      path: '/finance-hub',   roles: ['owner'] },
    { text: 'Content Audit Log',   icon: <AuditIcon />,           path: '/content/audit', roles: ['owner'] },
    { text: 'User Activity Log',   icon: <AuditIcon />,           path: '/user-activity', roles: ['owner'] },
  ];

  const getUserRoles = () => {
    const roles = new Set();
    if (user?.role) roles.add(user.role);
    try {
      const extra = Array.isArray(user?.roles) ? user.roles : JSON.parse(user?.roles || '[]');
      extra.forEach(r => roles.add(r));
    } catch {}
    return roles;
  };

  const userRoles = getUserRoles();
  const isOwnerOrManager = userRoles.has('owner') || userRoles.has('manager');

  const menuItems = allMenuItems.filter(item => {
    // RBAC (authoritative once the matrix has loaded): show item only if the
    // user can VIEW its module. Unknown paths fall back to the legacy roles.
    if (permsLoaded) {
      const allowed = canViewPath(item.path);
      if (allowed === true) return true;
      if (allowed === false) return false;
    }
    if (item.roles.includes('all')) return true;
    return item.roles.some(r => userRoles.has(r));
  });

  const groupedNav = isOwnerOrManager
    ? NAV_GROUPS.map(g => ({
        label: g.label,
        items: menuItems.filter(item => g.paths.includes(item.path))
      })).filter(g => g.items.length > 0)
    : null;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Logo area */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
        px: sidebarCollapsed ? 0 : 2, minHeight: 52, flexShrink: 0
      }}>
        {!sidebarCollapsed && (
          <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>🎬 Digital Hub</Typography>
        )}
        {sidebarCollapsed && (
          <Typography variant="h6" sx={{ fontWeight: 700 }}>🎬</Typography>
        )}
      </Box>
      <Divider />

      {/* Nav items */}
      <List sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, py: 0 }}>
        {isOwnerOrManager && groupedNav ? (
          <GroupedNav
            groups={groupedNav}
            navigate={navigate}
            location={location}
            isMobile={isMobile}
            setMobileOpen={setMobileOpen}
            collapsed={sidebarCollapsed}
          />
        ) : (
          menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const btn = (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  selected={isActive}
                  sx={{
                    py: 0.6,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    minHeight: 44, px: sidebarCollapsed ? 0 : 2,
                    '&.Mui-selected': { backgroundColor: 'primary.light', '&:hover': { backgroundColor: 'primary.light' } }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 36, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', lineHeight: 1.3 }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
            return sidebarCollapsed
              ? <Tooltip key={item.text} title={item.text} placement="right" arrow>{btn}</Tooltip>
              : btn;
          })
        )}
      </List>

      {/* Collapse / expand toggle button */}
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-end', p: 0.5 }}>
        <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton onClick={toggleSidebar} size="small" sx={{ borderRadius: 1 }}>
            {sidebarCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            🎬 JBDE – Digital Hub (டிஜிட்டல் மையம்)
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Clear Cache & Refresh">
              <IconButton onClick={() => { const u = new URL(window.location.href); u.searchParams.set('_v', Date.now()); window.location.replace(u.toString()); }} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Change Theme">
              <IconButton onClick={() => setThemeDialogOpen(true)} sx={{ color: 'white' }}>
                <PaletteIcon />
              </IconButton>
            </Tooltip>
            <Box textAlign="right" sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight={600}>{user?.fullName}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </Typography>
            </Box>
            <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 1 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}>
                {user?.fullName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box px={2} py={1}>
                <Typography variant="subtitle2">{user?.fullName}</Typography>
                <Typography variant="caption" color="textSecondary">{user?.email}</Typography>
                <Typography variant="caption" display="block" color="primary">{user?.role?.toUpperCase()}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleProfile}><ListItemIcon><Person fontSize="small" /></ListItemIcon>My Profile</MenuItem>
              <MenuItem onClick={handleChangePassword}><ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>Change Password</MenuItem>
              <MenuItem onClick={handleLogout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: sidebarWidth }, flexShrink: { md: 0 }, transition: 'width 0.2s' }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: SIDEBAR_FULL } }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: sidebarWidth,
                top: 64,
                height: 'calc(100% - 64px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                transition: 'width 0.2s ease',
              }
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1, p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${sidebarWidth}px)` },
          transition: 'width 0.2s ease',
        }}
      >
        <Toolbar />
        {children}
      </Box>

      <ChangePasswordDialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} />

      <Dialog open={themeDialogOpen} onClose={() => setThemeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>🎨 Choose Theme</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={currentTheme} onChange={(e) => setCurrentTheme(e.target.value)}>
              {themes.map((theme) => (
                <Box key={theme.id} sx={{ mb: 1 }}>
                  <FormControlLabel
                    value={theme.id}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: theme.primary, border: '2px solid', borderColor: currentTheme === theme.id ? 'primary.main' : 'grey.300' }} />
                        <Box>
                          <Typography variant="body1" fontWeight={currentTheme === theme.id ? 600 : 400}>{theme.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{theme.mode === 'dark' ? '🌙 Dark Mode' : '☀️ Light Mode'}</Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ border: '1px solid', borderColor: currentTheme === theme.id ? 'primary.main' : 'grey.300', borderRadius: 2, p: 1.5, m: 0, '&:hover': { bgcolor: 'action.hover' } }}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
          <Box mt={2}>
            <Button fullWidth variant="contained" onClick={() => setThemeDialogOpen(false)}>Done</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Layout;
